import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import './HeroCarousel.css';

const combineClassNames = (...classes) => classes.filter(Boolean).join(' ');

const slideAnimationPresets = {
  rise: {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -40 },
    transition: { duration: 0.65, ease: 'easeOut' }
  },
  'slide-left': {
    initial: { opacity: 0, x: -80 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 60 },
    transition: { duration: 0.7, ease: 'easeOut' }
  },
  'slide-right': {
    initial: { opacity: 0, x: 80 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -60 },
    transition: { duration: 0.7, ease: 'easeOut' }
  },
  zoom: {
    initial: { opacity: 0, scale: 0.9 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
    transition: { duration: 0.6, ease: 'easeOut' }
  }
};

function HeroCarousel({
  slides,
  interval = 6500,
  overlay,
  className = '',
  indicatorPosition = 'bottom-right',
  indicatorVariant = 'progress',
  showActiveLabel = false,
  showArrows = true,
  showSlideCaptions = false,
  captionAlignment = 'left'
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasFocus, setHasFocus] = useState(false);

  const slideCount = slides.length;
  const activeSlide = slides[activeIndex] ?? {};
  const captionAnimation = slideAnimationPresets[activeSlide.animation] || slideAnimationPresets.rise;

  const safeInterval = useMemo(() => Math.max(interval, 3000), [interval]);

  useEffect(() => {
    if (slideCount <= 1) {
      return undefined;
    }

    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % slideCount);
    }, safeInterval);

    return () => clearInterval(timer);
  }, [slideCount, safeInterval]);

  const goToSlide = (index) => {
    if (index === activeIndex) return;
    setActiveIndex(index);
  };

  const goNext = () => {
    setActiveIndex((prev) => (prev + 1) % slideCount);
  };

  const goPrev = () => {
    setActiveIndex((prev) => (prev - 1 + slideCount) % slideCount);
  };

  return (
    <Box
      component='section'
      className={combineClassNames('hero-carousel-root', className)}
      onMouseEnter={() => setHasFocus(true)}
      onMouseLeave={() => setHasFocus(false)}>
      <AnimatePresence mode='wait'>
        <motion.div
          key={activeSlide.image ?? activeIndex}
          className='hero-carousel-slide'
          style={{ backgroundImage: `url(${activeSlide.image})` }}
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
        />
      </AnimatePresence>

      <div className='hero-carousel-gradient hero-carousel-gradient--top' />
      <div className='hero-carousel-gradient hero-carousel-gradient--bottom' />
      <div className='hero-carousel-noise' />

      <Box className='hero-carousel-overlay'>{overlay}</Box>

      <AnimatePresence mode='wait'>
        {showSlideCaptions && (activeSlide.title || activeSlide.eyebrow || activeSlide.description || activeSlide.ctaText) && (
          <motion.div
            key={`caption-${activeSlide.image ?? activeIndex}`}
            className={combineClassNames('hero-carousel-caption', `hero-carousel-caption--${captionAlignment}`)}
            initial={captionAnimation.initial}
            animate={captionAnimation.animate}
            exit={captionAnimation.exit}
            transition={captionAnimation.transition}>
            {activeSlide.eyebrow && (
              <motion.p
                className='hero-carousel-caption-eyebrow'
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease: 'easeOut', delay: 0.05 }}>
                {activeSlide.eyebrow}
              </motion.p>
            )}
            {activeSlide.title && (
              <motion.h2
                className='hero-carousel-caption-title'
                initial={{ opacity: 0, y: 25 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, ease: 'easeOut', delay: 0.1 }}>
                {activeSlide.title}
              </motion.h2>
            )}
            {activeSlide.description && (
              <motion.p
                className='hero-carousel-caption-description'
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: 'easeOut', delay: 0.15 }}>
                {activeSlide.description}
              </motion.p>
            )}
            {activeSlide.ctaText && activeSlide.ctaHref && (
              <motion.a
                className='hero-carousel-caption-cta'
                href={activeSlide.ctaHref}
                target={activeSlide.ctaTarget ?? '_self'}
                rel={activeSlide.ctaTarget === '_blank' ? 'noopener noreferrer' : undefined}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: 'easeOut', delay: 0.25 }}>
                <span>{activeSlide.ctaText}</span>
                <span aria-hidden='true'>→</span>
              </motion.a>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {slideCount > 1 && indicatorVariant === 'progress' && (
        <Box className={combineClassNames('hero-carousel-indicators', `hero-carousel-indicators--${indicatorPosition}`)}>
          {slides.map((slide, index) => (
            <button
              key={slide.image ?? index}
              type='button'
              className={combineClassNames('hero-carousel-indicator', index === activeIndex && 'is-active')}
              onClick={() => goToSlide(index)}
              aria-label={`Show slide ${index + 1}`}>
              <span className='indicator-track'>
                <motion.span
                  className='indicator-progress'
                  initial={false}
                  animate={{ scaleX: index === activeIndex ? 1 : 0 }}
                  transition={{ duration: index === activeIndex ? safeInterval / 1000 : 0.35, ease: 'easeInOut' }}
                  style={{ transformOrigin: 'left center' }}
                />
              </span>
              {slide.label && (
                <Typography component='span' variant='caption' className='indicator-label'>
                  {slide.label}
                </Typography>
              )}
            </button>
          ))}
        </Box>
      )}

      {slideCount > 1 && indicatorVariant === 'dots' && (
        <Box className={combineClassNames('hero-carousel-dots', `hero-carousel-dots--${indicatorPosition}`)}>
          {slides.map((slide, index) => (
            <button
              key={slide.image ?? index}
              type='button'
              className={combineClassNames('hero-carousel-dot', index === activeIndex && 'is-active')}
              onClick={() => goToSlide(index)}
              aria-label={`Show slide ${index + 1}`}>
              <motion.span
                className='hero-carousel-dot-inner'
                initial={false}
                animate={{ scale: index === activeIndex ? 1 : 0.65, opacity: index === activeIndex ? 1 : 0.7 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
              />
            </button>
          ))}
        </Box>
      )}

      <AnimatePresence mode='wait'>
        <motion.span
          key={`reveal-${activeSlide.image ?? activeIndex}`}
          className='hero-carousel-reveal'
          initial={{ x: '-40%', opacity: 0 }}
          animate={{ x: '110%', opacity: [0, 0.45, 0] }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.25, ease: 'easeOut' }}
        />
      </AnimatePresence>

      <AnimatePresence mode='wait'>
        {showActiveLabel && activeSlide?.label && (
          <motion.div
            key={`label-${activeSlide.label}`}
            className='hero-carousel-active-label'
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}>
            <Typography variant='caption' component='span'>
              {activeSlide.label}
            </Typography>
          </motion.div>
        )}
      </AnimatePresence>
      {slideCount > 1 && showArrows && (
        <Box className='hero-carousel-nav'>
          <button type='button' className='hero-carousel-nav-button hero-carousel-nav-button--left' aria-label='Previous slide' onClick={goPrev}>
            <span aria-hidden='true'>
              <svg width='24' height='16' viewBox='0 0 24 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path d='M17.5 1.5L6.5 8L17.5 14.5' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
              </svg>
            </span>
          </button>
          <button type='button' className='hero-carousel-nav-button hero-carousel-nav-button--right' aria-label='Next slide' onClick={goNext}>
            <span aria-hidden='true'>
              <svg width='24' height='16' viewBox='0 0 24 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path d='M6.5 14.5L17.5 8L6.5 1.5' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
              </svg>
            </span>
          </button>
        </Box>
      )}

      <motion.div className='hero-carousel-focus-ring' animate={{ opacity: hasFocus ? 1 : 0 }} transition={{ duration: 0.4, ease: 'easeOut' }} />
    </Box>
  );
}

HeroCarousel.propTypes = {
  slides: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.string.isRequired,
      label: PropTypes.string,
      eyebrow: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
      animation: PropTypes.oneOf(['rise', 'slide-left', 'slide-right', 'zoom']),
      ctaText: PropTypes.string,
      ctaHref: PropTypes.string,
      ctaTarget: PropTypes.string
    })
  ).isRequired,
  interval: PropTypes.number,
  overlay: PropTypes.node,
  className: PropTypes.string,
  indicatorPosition: PropTypes.oneOf(['bottom-right', 'bottom-left', 'bottom-center']),
  indicatorVariant: PropTypes.oneOf(['progress', 'dots']),
  showActiveLabel: PropTypes.bool,
  showArrows: PropTypes.bool,
  showSlideCaptions: PropTypes.bool,
  captionAlignment: PropTypes.oneOf(['left', 'center', 'right'])
};

HeroCarousel.defaultProps = {
  interval: 6500,
  overlay: null,
  className: '',
  indicatorPosition: 'bottom-right',
  indicatorVariant: 'progress',
  showActiveLabel: false,
  showArrows: true,
  showSlideCaptions: false,
  captionAlignment: 'left'
};

export default HeroCarousel;
